import * as T from 'three';
import type {PlanetHost} from '../world/planetHost';
import {cliffHit} from '@hyperlight/shared';

export const WALL_RUN={
  minSpeed:4.5,minAlong:3.2,maxApproachNormal:Math.sin(Math.PI/3),
  runSpeed:10.8,coastSpeed:2.6,speedFloor:13.8,maxSpeed:26.4,carrySpeedScale:1.2,
  acceleration:14.4,deceleration:18,dropSpeed:5.2,gravityScale:.85,fallSpeed:3,coastFallSpeed:9,
  reach:1.2,gap:.88,duration:2.5,entry:.18,jumpDuration:.73,cooldown:.55,jumpOut:6.5,jumpUp:7.8,
  jumpMaxNormalFraction:.87,jumpBackCarry:.8,jumpPitchGain:4,jumpMinUp:5.3,jumpMaxUp:10.3,
} as const;
/** The signed phase travels over the wire: left negative, right positive;
 * 1 catches the wall, 2 runs, 3 plays the jump-off. Camera yaw stays independent. */
export class WallRunMotion {
  state=0;
  age=0;
  private elapsed=0;
  private cooldown=0;
  private speed=0;
  private entrySpeed=0;
  private exhausted=false;
  private rise=0;
  private jumpTransfer=false;
  private readonly jumpNormal=new T.Vector3();
  private readonly jumpOrigin=new T.Vector3();
  readonly normal=new T.Vector3();
  readonly tangent=new T.Vector3();
  private readonly local=new T.Vector3();
  private readonly delta=new T.Vector3();
  private readonly surface=new T.Vector3();
  private readonly lower=new T.Vector3();
  private readonly hit=new T.Vector3();
  private readonly ahead=new T.Vector3();
  private readonly horizontal=new T.Vector3();
  private readonly jumpLook=new T.Vector3();
  private readonly out={x:0,y:0,z:0};
  private readonly cliff=cliffHit();
  private readonly point=[0,0,0];
  private readonly terrainOrigin=new T.Vector3();
  private readonly terrainRay=new T.Vector3();
  private readonly terrainPoint=new T.Vector3();
  private readonly terrainDir=new T.Vector3();
  private readonly terrainNormal=new T.Vector3();
  private readonly terrainSide=new T.Vector3();
  private readonly terrainForward=new T.Vector3();
  private readonly terrainNormalArray=[0,0,0];
  get attached():boolean{return Math.abs(this.state)===1||Math.abs(this.state)===2;}
  reset():void{this.state=0;this.age=0;this.elapsed=0;this.cooldown=0;this.exhausted=false;this.jumpTransfer=false;}
  /** Touchdown ends an airborne transfer; settled feet restore the run budget. */
  landed(rearm=true):void{this.jumpTransfer=false;if(rearm)this.exhausted=false;}
  release():void{this.state=0;this.age=0;this.cooldown=WALL_RUN.cooldown;this.jumpTransfer=false;}

  /** Uses the same analytic solid field as foot collision. Two height samples
   * and a leading sample reject roofs, short rails, corners and wall endings.
   * Natural cliffs and solid scatter trunks use their existing collision
   * fields too. Foliage/canopies never supply a wall. */
  private probe(host:PlanetHost,pos:T.Vector3,up:T.Vector3,out:T.Vector3):number {
    let best=Infinity;
    for(let source=0;source<4;source++){
      let distance=0,valid=true;
      for(const height of [.4,1.25]){
        this.local.copy(pos).addScaledVector(up,height).sub(host.pose.center).applyQuaternion(host.pose.invQuat);
        const {x,y,z}=this.local;
        if(source===3){
          this.point[0]=x;this.point[1]=y;this.point[2]=z;
          const contact=host.terrainSolids?.capsuleContact(this.point,this.point,WALL_RUN.reach,this.cliff);
          if(!contact){valid=false;break;}
          this.out.x=x+contact.normal[0]*contact.penetration;
          this.out.y=y+contact.normal[1]*contact.penetration;
          this.out.z=z+contact.normal[2]*contact.penetration;
        }
        const hit=source===0?host.settlementColliders?.resolve(x,y,z,WALL_RUN.reach,this.out)
          :source===1?host.resolveHomesteadCollision?.(x,y,z,WALL_RUN.reach,this.out)
          :source===2?host.resolveScatterCollision?.(x,y,z,WALL_RUN.reach,this.out):true;
        if(!hit){valid=false;break;}
        this.delta.set(this.out.x-x,this.out.y-y,this.out.z-z).applyQuaternion(host.pose.quat);
        const push=this.delta.length();if(push<1e-5){valid=false;break;}
        this.delta.divideScalar(push);
        if(Math.abs(this.delta.dot(up))>.35){valid=false;break;}
        // Push-out is capped by the real world collision field. It supplies
        // a normal, not a trustworthy distance: a close thin wall could be
        // overshot by the former material probe. Trace to its actual face.
        this.surface.copy(this.delta).applyQuaternion(host.pose.invQuat)
          .multiplyScalar(-WALL_RUN.reach).add(this.local);
        const trace=source===0?host.settlementColliders?.segmentHit(x,y,z,this.surface.x,this.surface.y,this.surface.z)
          :source===1?host.homesteadSegmentHit?.(x,y,z,this.surface.x,this.surface.y,this.surface.z,0):undefined;
        let sampleDistance=WALL_RUN.reach-push;
        if(trace!==undefined){
          if(trace<0){valid=false;break;}
          sampleDistance=WALL_RUN.reach*trace;
        }else{
          // Scatter push-out is uncapped. Its zero-radius material check also
          // rejects canopies and low stumps at the two sampled body heights.
          this.surface.copy(this.delta).applyQuaternion(host.pose.invQuat)
            .multiplyScalar(-(sampleDistance+.015)).add(this.local);
          const solid=source===1?host.homesteadOccludes?.(this.surface.x,this.surface.y,this.surface.z,0)
            :source===2?host.resolveScatterCollision?.(this.surface.x,this.surface.y,this.surface.z,0,this.out)
            :this.cliff.penetration<WALL_RUN.reach+.05;
          if(!solid){valid=false;break;}
        }
        if(height===.4){this.lower.copy(this.delta);distance=sampleDistance;}
        else if(this.lower.dot(this.delta)<.97){valid=false;break;}
      }
      if(valid&&distance<best){best=distance;out.copy(this.lower).addScaledVector(up,-this.lower.dot(up)).normalize();}
    }
    if(!Number.isFinite(best))best=this.probeTerrain(host,pos,up,out);
    return best;
  }

  /** Horizontal material tests against the authoritative spherical heightfield.
   * A heightfield's radius under the feet is not a sideways wall contact: trace
   * toward it, then reject ordinary walkable slopes using its actual normal. */
  private terrainDistance(host:PlanetHost,t:number):number {
    this.terrainPoint.copy(this.terrainOrigin).addScaledVector(this.terrainRay,t);
    this.terrainDir.copy(this.terrainPoint).sub(host.pose.center);
    const radius=this.terrainDir.length();this.terrainDir.divideScalar(radius);
    return radius-host.surfaceRadiusWorld(this.terrainDir);
  }
  private terrainEntry(host:PlanetHost):number {
    if(this.terrainDistance(host,WALL_RUN.reach)>0)return Infinity;
    if(this.terrainDistance(host,0)<-.05)return Infinity;
    let lo=0,hi:number=WALL_RUN.reach;
    for(let i=0;i<7;i++){const mid=(lo+hi)/2;if(this.terrainDistance(host,mid)>0)lo=mid;else hi=mid;}
    this.terrainDistance(host,hi);
    host.surfaceNormalWorld(this.terrainDir,this.terrainNormalArray,.15);
    this.terrainNormal.fromArray(this.terrainNormalArray).normalize();
    return hi;
  }
  private probeTerrain(host:PlanetHost,pos:T.Vector3,up:T.Vector3,out:T.Vector3):number {
    if(this.horizontal.lengthSq()<1e-8)return Infinity;
    this.terrainForward.copy(this.horizontal).normalize();
    this.terrainSide.crossVectors(this.terrainForward,up).normalize();
    let best=Infinity;
    for(let i=0;i<8;i++){
      const angle=i*Math.PI/4;
      this.terrainRay.copy(this.terrainSide).multiplyScalar(Math.cos(angle)).addScaledVector(this.terrainForward,Math.sin(angle));
      this.terrainOrigin.copy(pos).addScaledVector(up,.4);
      const entry=this.terrainEntry(host);
      if(!Number.isFinite(entry)||Math.abs(this.terrainNormal.dot(up))>.35)continue;
      this.lower.copy(this.terrainNormal).addScaledVector(up,-this.terrainNormal.dot(up)).normalize();
      const distance=entry*-this.lower.dot(this.terrainRay);
      if(distance<0||distance>=best)continue;
      this.terrainOrigin.copy(pos).addScaledVector(up,1.25);
      // A tall wall must also exist at shoulder height; no knee-high banks.
      const upper=this.terrainEntry(host);
      if(!Number.isFinite(upper)||Math.abs(this.terrainNormal.dot(up))>.35||this.lower.dot(this.terrainNormal)<.93)continue;
      best=distance;out.copy(this.lower);
    }
    return best;
  }

  step(dt:number,host:PlanetHost,pos:T.Vector3,vel:T.Vector3,up:T.Vector3,enabled:boolean,jump:boolean,gravity:number,clearance:number=WALL_RUN.gap,approach?:T.Vector3,driving=true,sprinting=true,look?:T.Vector3):'run'|'jump'|'' {
    this.cooldown=Math.max(0,this.cooldown-dt);
    if(Math.abs(this.state)===3){
      this.age+=dt;
      if(!enabled){this.release();return '';}
      if(this.age>=WALL_RUN.jumpDuration){
        // Keep the original same-wall cooldown after the jump clip. A real
        // opposing wall may still take ownership before that cooldown ends.
        this.state=0;this.age=0;this.cooldown=WALL_RUN.cooldown;
      }
    }
    if(!enabled){if(this.attached||this.jumpTransfer)this.release();return '';}
    if(!host.sampler||!host.pose.invQuat)return '';
    // Timeouts require real separation (or a landing). A brief cooldown alone
    // lets a falling player re-catch the same wall indefinitely.
    if(this.exhausted){
      if(Number.isFinite(this.probe(host,pos,up,this.hit)))return '';
      this.exhausted=false;
    }
    this.horizontal.copy(vel).addScaledVector(up,-vel.dot(up));
    if(!this.attached&&(this.horizontal.length()<WALL_RUN.minSpeed||vel.dot(up)<-20))return '';
    const distance=this.probe(host,pos,up,this.hit);
    if(!Number.isFinite(distance)){if(this.attached)this.release();return '';}
    // A real jump to an opposing surface carries its own approach direction.
    // The player may still be looking/steering across the gap at contact.
    const oppositeTransfer=!this.attached&&this.jumpTransfer&&this.jumpNormal.dot(this.hit)<-.94
      &&this.delta.copy(pos).sub(this.jumpOrigin).dot(this.jumpNormal)>WALL_RUN.reach;
    if(!this.attached&&(Math.abs(this.state)===3||this.cooldown>0)){
      // Signed animation phase alone cannot identify a different wall (turning
      // around flips the sign). Require an opposing physical surface normal
      // and actual travel away from the wall that supplied the jump impulse.
      if(!oppositeTransfer)return '';
    }
    if(this.attached){
      if(this.elapsed+dt>WALL_RUN.duration+1e-6){
        this.exhausted=true;this.release();
        // No jump-off impulse or jump phase: hand the carried speed to gravity.
        vel.addScaledVector(up,-Math.max(0,vel.dot(up)));
        return '';
      }
      if(this.normal.dot(this.hit)<.94){this.release();return '';}
      this.normal.copy(this.hit);
      this.tangent.addScaledVector(this.normal,-this.tangent.dot(this.normal)).normalize();
    }else{
      const normalSpeed=this.horizontal.dot(this.hit);
      if(normalSpeed>2||Math.abs(normalSpeed)>this.horizontal.length()*WALL_RUN.maxApproachNormal)return '';
      // Collision can strip a head-on velocity into a sideways slide. Also
      // test movement intent so holding into the wall cannot manufacture a catch.
      if(approach&&!oppositeTransfer){
        this.delta.copy(approach).addScaledVector(up,-approach.dot(up));
        if(Math.abs(this.delta.dot(this.hit))>this.delta.length()*WALL_RUN.maxApproachNormal)return '';
      }
      this.tangent.copy(this.horizontal).addScaledVector(this.hit,-normalSpeed);
      if(this.tangent.length()<WALL_RUN.minAlong)return '';
      this.speed=T.MathUtils.clamp(this.tangent.length(),7,WALL_RUN.maxSpeed);
      this.entrySpeed=this.speed;this.tangent.normalize();
      this.normal.copy(this.hit);
      this.jumpTransfer=false;
      // Right = travel × up. An outward normal pointing right means wall LEFT.
      this.state=this.delta.crossVectors(this.tangent,up).dot(this.normal)>0?-1:1;
      this.age=0;this.elapsed=0;this.rise=T.MathUtils.clamp(vel.dot(up),2.5,3.2);
      // The Space that approaches a wall is never also the departure press.
      jump=false;
    }
    // A short leading sample follows curved trunks without bridging sharp
    // building corners or a gap at the end of a wall.
    this.ahead.copy(pos).addScaledVector(this.tangent,.25);
    const forwardDistance=this.probe(host,this.ahead,up,this.hit);
    if(!Number.isFinite(forwardDistance)||this.normal.dot(this.hit)<.94){this.release();return '';}
    if(jump){
      this.state=Math.sign(this.state)*3;this.age=0;this.cooldown=WALL_RUN.cooldown;
      this.jumpTransfer=true;this.jumpNormal.copy(this.normal);this.jumpOrigin.copy(pos);
      let along=this.speed,outward:number=WALL_RUN.jumpOut,lift:number=WALL_RUN.jumpUp;
      if(look&&Number.isFinite(look.x)&&Number.isFinite(look.y)&&Number.isFinite(look.z)&&look.lengthSq()>1e-8){
        this.jumpLook.copy(look).normalize();
        const lookUp=this.jumpLook.dot(up);
        lift=T.MathUtils.clamp(WALL_RUN.jumpUp+lookUp*WALL_RUN.jumpPitchGain,WALL_RUN.jumpMinUp,WALL_RUN.jumpMaxUp);
        this.jumpLook.addScaledVector(up,-lookUp);
        if(this.jumpLook.lengthSq()>1e-8){
          this.jumpLook.normalize();
          const away=Math.max(0,this.jumpLook.dot(this.normal)),lookAlong=this.jumpLook.dot(this.tangent);
          const budget=Math.hypot(this.speed,WALL_RUN.jumpOut);
          // Aim in the outward hemisphere without creating horizontal energy.
          // A 60-degree look gives a 60-degree departure; near-perpendicular
          // looks stop at about 60.5 degrees, retaining carry for an opposing catch.
          // Slow coasting retains its original minimum separation instead.
          outward=T.MathUtils.clamp(budget*away,WALL_RUN.jumpOut,Math.max(WALL_RUN.jumpOut,budget*WALL_RUN.jumpMaxNormalFraction));
          along=Math.sqrt(Math.max(0,budget*budget-outward*outward));
          // Avoid flipping travel on numerical noise when looking straight
          // across the gap. A deliberate backward look can reverse the jump.
          if(lookAlong<-.05)along=-Math.min(along,this.speed*WALL_RUN.jumpBackCarry);
        }
      }
      vel.copy(this.tangent).multiplyScalar(along).addScaledVector(this.normal,outward).addScaledVector(up,lift);
      return 'jump';
    }
    this.elapsed+=dt;this.age+=dt;
    if(Math.abs(this.state)===1&&this.age>=WALL_RUN.entry){this.state=Math.sign(this.state)*2;this.age=0;}
    // Once attached, pressing into the wall follows the established run rather
    // than draining its forward drive while the camera turns. Neutral/outward
    // input can still coast; reverse/dive cancels remain owned by the caller.
    const drive=driving?(approach?T.MathUtils.clamp(approach.dot(this.tangent)+Math.max(0,-approach.dot(this.normal)),0,1):1):0;
    const targetSpeed=Math.max(WALL_RUN.coastSpeed,T.MathUtils.clamp(this.entrySpeed*WALL_RUN.carrySpeedScale*(sprinting?1.15:1),sprinting?WALL_RUN.speedFloor:WALL_RUN.runSpeed,WALL_RUN.maxSpeed)*drive);
    this.speed+=T.MathUtils.clamp(targetSpeed-this.speed,-WALL_RUN.deceleration*dt,WALL_RUN.acceleration*dt);
    // Releasing movement decelerates into a descending coast; it does not
    // cancel an attached run. Forward carry and gravity keep the body moving,
    // while the existing 2.5s budget, wall ends and explicit cancels still exit.
    const support=drive*T.MathUtils.smoothstep(this.speed,WALL_RUN.dropSpeed,WALL_RUN.speedFloor);
    this.rise=Math.max(T.MathUtils.lerp(-WALL_RUN.coastFallSpeed,-WALL_RUN.fallSpeed,support),this.rise-gravity*WALL_RUN.gravityScale*T.MathUtils.lerp(1,.22,support)*dt);
    this.tangent.addScaledVector(up,-this.tangent.dot(up)).normalize();
    // A short tether catches the surface without teleporting the player onto it.
    vel.copy(this.tangent).multiplyScalar(this.speed).addScaledVector(up,this.rise)
      .addScaledVector(this.normal,T.MathUtils.clamp((clearance-distance)*18,-5,5));
    return 'run';
  }
}
